import { FC, FormEvent, useEffect, useState } from 'react';
import { importAllIcons } from './ManageTaskDialog';
import axiosInstance from 'config/axiosInstance';
import { useTranslation } from 'react-i18next';

interface AddNewTaskDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const iconsPerPage = 20;

const AddNewTaskDialog: FC<AddNewTaskDialogProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation('client');
  const [taskName, setTaskName] = useState<string>('');
  const [selectedIcon, setSelectedIcon] = useState<string>('');
  const [iconName, setIconName] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [icons, setIcons] = useState<{ [key: string]: string }>({});
  const [isTouched, setIsTouched] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean>(true);

  useEffect(() => {
    const fetchIcons = async () => {
      const importedIcons = await importAllIcons();
      setIcons(importedIcons);
      const firstIconName = Object.keys(importedIcons)[0];
      setSelectedIcon(importedIcons[firstIconName]);
      setIconName(firstIconName);
    };

    fetchIcons();
  }, []);

  if (!isOpen) return null;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\s/g, '');
    setTaskName(value);
    setIsValid(!!e.target.value.trim());
  };

  const handleIconClick = (name: string) => {
    setSelectedIcon(icons[name]);
    setIconName(name);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsTouched(true);

    if (!taskName.trim()) {
      setIsValid(false);
      return;
    }

    try {
      await axiosInstance.post(`/v1/tasks/`, {
        name: taskName,
        icon: iconName,
      });
    } catch (error) {
      console.error('Error adding new task:', error);
    }

    onClose();
  };

  const handleNextPage = () => {
    if (currentPage < Math.ceil(Object.keys(icons).length / iconsPerPage)) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };

  const startIndex = (currentPage - 1) * iconsPerPage;
  const currentIcons = Object.entries(icons).slice(
    startIndex,
    startIndex + iconsPerPage
  );

  return (
    <div className="fixed z-10 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>
        <div
          className="inline-block align-middle bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 w-[804px]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-headline"
          tabIndex={-1}
        >
          <div className="bg-white">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-left sm:mt-0 w-full">
                <div className="flex justify-between items-center mt-4 mx-6">
                  <h3
                    className="text-subtitle-1 font-medium text-neutral/300"
                    id="modal-headline"
                  >
                    {t('add_task.title')}
                  </h3>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <span className="sr-only">Close</span>
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <hr className="border-gray-300 mt-4 w-full" />
                <form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-4 gap-x-4 gap-y-4 p-6 h-56">
                    <div className="h-40 flex-col col-span-1 justify-start items-center gap-2 inline-flex">
                      <div className="p-4 bg-white/20 rounded-lg border-2 border-[#999996] justify-center items-center">
                        <img src={selectedIcon} className="w-20 h-20" />
                      </div>
                      <div className="gap-y-2">
                        <input
                          type="text"
                          maxLength={10}
                          placeholder={t('add_task.placeholder')}
                          value={taskName}
                          onChange={handleInputChange}
                          className={`w-full h-10 p-4 text-center text-body-2 border rounded-md focus:outline-none
    ${!isValid && isTouched ? 'border-error' : 'border-neutral/50'}
    ${
      !isValid && isTouched ? 'focus:border-error' : 'focus:border-neutral/75'
    }`}
                        />
                        {!isValid && (
                          <div className="text-red-500 text-overline w-full text-start whitespace-nowrap">
                            {t('add_task.error')}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="w-full col-span-3">
                      <div className="grid grid-cols-10 gap-2">
                        {currentIcons.map(([name, path], index) => (
                          <div
                            key={index}
                            onClick={() => handleIconClick(name)}
                            className={`flex justify-center items-center w-12 h-12 cursor-pointer rounded-md ${
                              selectedIcon === path ? 'bg-primary/50' : 'border'
                            }`}
                          >
                            <img
                              src={path}
                              alt={`icon-${name}`}
                              className="w-8 h-8"
                            />
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-end space-x-3 mt-4">
                        <button
                          type="button"
                          className={`w-20 h-8 border border-neutral/50 text-neutral/300 rounded-md ${
                            currentPage === 1
                              ? 'cursor-not-allowed opacity-50 text-neutral/75'
                              : ''
                          }`}
                          onClick={handlePrevPage}
                          disabled={currentPage === 1}
                        >
                          &lt; {t('action.back')}
                        </button>
                        <button
                          type="button"
                          className={`w-20 h-8 border border-neutral/50 text-neutral/300 rounded-md ${
                            currentPage ===
                            Math.ceil(Object.keys(icons).length / iconsPerPage)
                              ? 'cursor-not-allowed opacity-50'
                              : ''
                          }`}
                          onClick={handleNextPage}
                          disabled={
                            currentPage ===
                            Math.ceil(Object.keys(icons).length / iconsPerPage)
                          }
                        >
                          {t('action.next')} &gt;
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="px-4 py-3 border-t-[1px] grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      className="w-full inline-flex justify-center items-center rounded-md border bg-primary/50 text-subtitle-2 text-primary/300"
                      onClick={onClose}
                    >
                      {t('action.cancel')}
                    </button>
                    <button
                      type="submit"
                      className="w-full inline-flex justify-center items-center rounded-md bg-primary/300 text-subtitle-2 text-white h-12"
                    >
                      {t('action.add_task')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddNewTaskDialog;
